/* @flow */

import HashMap from '../../protocol/HashMap';
import t from '../../i18n/I18n';
import StringUtil from '../../util/StringUtil';

/**
 * Class is analogous to java enum class.
 * Values must be same and name as java class else it will not wokr.
 */

export default class CollectionFeature {
  static id2Enum: Map<number, CollectionFeature> = new Map();
  _name: string;
  _id: number;

  constructor(name: string, id: number) {
    this._name = name;
    this._id = id;
    CollectionFeature.id2Enum.set(id, this);
  }

  get id(): number {
    return this._id;
  }

  toString(): string {
    return this._name;
  }

  i18n(): string {
    const name: string = this.toString();
    return StringUtil.toFirstUpper(t(name).toLowerCase());
  }

  label(): string {
    return this.i18n();
  }

  static toEnum(id: number): CollectionFeature {
    //Don't inline. It will cause flow to not resolve type
    // noinspection UnnecessaryLocalVariableJS
    let cf: any = CollectionFeature.id2Enum.get(id);
    return cf;
  }

  static toArray(map?: HashMap<number, boolean>): Array<CollectionFeature> {
    const features: Array<CollectionFeature> = [];
    if (map) {
      const hashMapIterator = map.iterator();

      while (hashMapIterator.hasNext()) {
        const mapEntry = hashMapIterator.next();
        const key: number = mapEntry.getKey();
        const value: boolean = mapEntry.getValue();
        if (value) {
          const cf: ?CollectionFeature = CollectionFeature.id2Enum.get(key);
          if (cf) {
            features.push(cf);
          }
        }
      }
    }
    return features;
  }

  static hasTrue(features: HashMap<number, boolean>, feature: CollectionFeature): boolean {
    if (!(features && feature)) {
      return false;
    }

    return features.get(feature.id) === true;
  }

  static contains(
    features: Array<CollectionFeature> | HashMap<number | CollectionFeature, boolean>,
    feature: CollectionFeature
  ): boolean {
    if (features) {
      if (Array.isArray(features)) {
        for (const item of features) {
          if (item.id === feature.id) {
            return true;
          }
        }
      } else if (features instanceof HashMap) {
        const keys: Array<number | CollectionFeature> = features.keys();
        for (const key of keys) {
          if (typeof key === 'number') {
            const newVar: ?CollectionFeature = CollectionFeature.id2Enum.get(key);
            if (newVar) {
              if (newVar.id === feature.id && features.get(feature.id) === true) {
                return true;
              }
            }
          } else if (key instanceof CollectionFeature) {
            if (key.id === feature.id) {
              return true;
            }
          }
        }
      }
    }
    return false;
  }

  static toHashMap(collectionFeatures: Array<CollectionFeature>): HashMap<number, boolean> {
    const map: HashMap<number, boolean> = new HashMap();
    for (const collectionFeature of collectionFeatures) {
      map.put(collectionFeature.id, true);
    }
    return map;
  }

  static ADMIN: CollectionFeature;
  static ADD_ASSETS: CollectionFeature;
  static WRITE_COMMENTS: CollectionFeature;
  static CAN_VIEW_ASSETS: CollectionFeature;
  static CAN_SHARE_COLLECTION: CollectionFeature;
  static CAN_DELETE_COLLECTION: CollectionFeature;
  static VIEW_COMMENTS: CollectionFeature;
  static SUPER_ADMIN: CollectionFeature;
  static OWNER: CollectionFeature;
  static MUTE_EMAIL_NOTIFICATIONS: CollectionFeature;
  static PASSWORD_PROTECTED: CollectionFeature;
  static id: number = 0;
  static nextId(): number {
    return ++CollectionFeature.id;
  }
}

CollectionFeature.ADMIN = new CollectionFeature('ADMIN', CollectionFeature.nextId());
CollectionFeature.ADD_ASSETS = new CollectionFeature('ADD_ASSETS', CollectionFeature.nextId());
CollectionFeature.WRITE_COMMENTS = new CollectionFeature('WRITE_COMMENTS', CollectionFeature.nextId());
CollectionFeature.CAN_VIEW_ASSETS = new CollectionFeature('CAN_VIEW_ASSETS', CollectionFeature.nextId());
CollectionFeature.CAN_SHARE_COLLECTION = new CollectionFeature('CAN_SHARE_COLLECTION', CollectionFeature.nextId());
CollectionFeature.CAN_DELETE_COLLECTION = new CollectionFeature('CAN_DELETE_COLLECTION', CollectionFeature.nextId());
CollectionFeature.VIEW_COMMENTS = new CollectionFeature('VIEW_COMMENTS', CollectionFeature.nextId());
CollectionFeature.SUPER_ADMIN = new CollectionFeature('SUPER_ADMIN', CollectionFeature.nextId());
CollectionFeature.OWNER = new CollectionFeature('OWNER', CollectionFeature.nextId());
CollectionFeature.MUTE_EMAIL_NOTIFICATIONS = new CollectionFeature('MUTE_EMAIL_NOTIFICATIONS', CollectionFeature.nextId());
CollectionFeature.PASSWORD_PROTECTED = new CollectionFeature('PASSWORD_PROTECTED', CollectionFeature.nextId());
